import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material"
import { useEffect, useMemo, useState } from "react"
import Iconify from "minimals-template/components/Iconify"
import { Bound } from "lib/@components/binding/Bound"
import { BoundAutocomplete } from "lib/@components/binding/bound-components"
import { useRefresh } from "lib/@hooks/useRefresh"
import { getMaintenanceDirectories } from "../../controller/maintenance-library"
import { SelectionIcon } from "./selection-icon"
import { MoveButton } from "./move-button"

export function MoveTaskDialog({ open, onClose, task, categoryId, onMoved }) {
    const results = getMaintenanceDirectories.useResults()
    const directories = useMemo(() => results || [], [results])
    const options = useMemo(getOptions, [directories, categoryId])
    const [target] = useState(() => ({ newCategoryId: "" }))
    const refresh = useRefresh()

    useEffect(resetOnOpen, [open])

    if (!task) return null

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <Bound target={target} refresh={refresh}>
                <DialogTitle>Move "{task.heading}"</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <Typography variant="body2">Select the destination folder for this component.</Typography>
                        <BoundAutocomplete
                            field="newCategoryId"
                            label="Destination Folder"
                            options={options}
                            autoFocus
                            sideEffects
                            extract={extractOption}
                            isOptionEqualToValue={isOptionEqual}
                            InputProps={{
                                startAdornment: <SelectionIcon />,
                            }}
                            renderOption={renderOption}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <MoveButton task={task} onMoved={onMoved} />
                </DialogActions>
            </Bound>
        </Dialog>
    )

    function resetOnOpen() {
        if (open) {
            target.newCategoryId = ""
            refresh()
        }
    }

    function getOptions() {
        return directories
            .filter((d) => d.id !== categoryId)
            .map((d) => {
                const depth = d.path ? d.path.split("/").length - 1 : 0
                const indent = "\u00A0\u00A0\u00A0\u00A0".repeat(depth)
                return {
                    id: d.id,
                    name: d.name,
                    depth,
                    label: `${indent}${d.name}`,
                }
            })
    }
}

function extractOption(e, v) {
    return v
}

function isOptionEqual(option, value) {
    return option.id === (value?.id || value)
}

function renderOption(props, option) {
    return (
        <Box component="li" {...props} sx={{ display: "flex", alignItems: "center" }}>
            {"\u00A0\u00A0\u00A0\u00A0".repeat(option.depth)}
            <Iconify
                icon="eva:folder-fill"
                sx={{ mr: 1, width: 20, height: 20, color: "warning.main", flexShrink: 0 }}
            />
            {option.name}
        </Box>
    )
}
